// ============================================================
// FILE: _services/stethoscope/stethoscope-manager.service.ts
//
// Ported from AaboOneRecorder (a separate Angular 20 app at
// /Users/ezovion/Documents/AaboOneRecorder) — its recorder-manager.service.ts.
// The "Aabo One" stethoscope is NOT a custom BLE/Serial device: it's a
// USB-C/Bluetooth device the OS exposes as a plain system microphone.
// Pairing itself happens at the OS level (outside any Web API this app can
// call); this service only ever talks to it through standard
// navigator.mediaDevices (enumerateDevices/getUserMedia) + MediaRecorder,
// exactly like the source app. That makes this service structurally
// different from every other manager service in this fleet (X6/Fetal
// Doppler's Web Bluetooth GATT, ECG Standalone 11's Web Serial, Aabo
// Scope's WebSocket bridge) — there is no GATT/serial protocol here at all.
//
// Ported behavior from the source's RecorderManagerService: enumerateDevices()
// restricted to audioinput, the devicechange hot-plug listener, the
// retry-once-on-deviceId-mismatch logic in openStreamForDevice() (the OS/
// browser sometimes silently substitutes the default mic instead of
// honoring the exact requested deviceId, especially right after a USB
// device is plugged in), and pickMimeType()'s codec-candidate list. Source's
// live-monitoring ("listen live" speaker passthrough) and local IndexedDB
// persistence were intentionally NOT ported — this app persists a finished
// recording as a visit attachment instead (see TriageComponent's
// uploadTriageAttachment()), and the user's own scope for this port was
// "live audio session only", not a listen-live monitor.
//
// MediaRecorder/BlobEvent aren't in this app's pinned TS 3.9 DOM lib the
// same way ResizeObserver isn't (see triage.component.ts's ngAfterViewInit()
// comment) -- referenced via `any` locally rather than a global typings
// change, matching how the codebase already handles other DOM APIs missing
// from that lib (e.g. `(navigator as any).bluetooth` in x6-manager.service.ts).
// ============================================================
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { StethoscopeState } from '../../@core/interfaces/device-integration/device-item.model';

const DEVICE_SETTLE_DELAY_MS = 400;

const INITIAL_STATE: StethoscopeState = {
  status: 'idle',
  devices: [],
  selectedDeviceId: null,
};

@Injectable({
  providedIn: 'root'
})
export class StethoscopeManagerService {

  private readonly stateSubject = new BehaviorSubject<StethoscopeState>(INITIAL_STATE);
  readonly state$: Observable<StethoscopeState> = this.stateSubject.asObservable();

  private knownDeviceIds = new Set<string>();
  private hasEnumeratedOnce = false;
  private mediaStream: MediaStream | null = null;
  private mediaRecorder: any = null;
  private recordedChunks: Blob[] = [];
  private recordingMimeType = '';
  private startedAtMs = 0;
  private stopPromise: Promise<void> | null = null;

  private readonly boundRefreshDevices = () => this.refreshDevices();

  /** Requests mic permission (if not already granted) and does the first device enumeration; wires devicechange for hot-plug detection from then on. Safe to call more than once (e.g. every time the connect modal opens) — refreshDevices() itself is idempotent. */
  async init(): Promise<void> {
    if (!(navigator as any).mediaDevices || !(navigator as any).mediaDevices.getUserMedia) {
      this.setState({ ...this.stateSubject.value, status: 'error', errorMessage: 'This browser does not support audio recording. Use Chrome or Edge.' });
      return;
    }
    if (!this.hasEnumeratedOnce) {
      (navigator as any).mediaDevices.addEventListener('devicechange', this.boundRefreshDevices);
    }
    try {
      const tempStream = await (navigator as any).mediaDevices.getUserMedia({ audio: true });
      tempStream.getTracks().forEach((t: any) => t.stop());
    } catch (err) {
      this.setState({ ...this.stateSubject.value, status: 'error', errorMessage: this.describeMediaError(err) });
      return;
    }
    await this.refreshDevices();
  }

  /** The "connect" action -- persists which enumerated audio input is the stethoscope. Called from StethoscopeConnectModalComponent once the clinician picks one from the list. */
  selectDevice(deviceId: string): void {
    this.setState({ ...this.stateSubject.value, selectedDeviceId: deviceId, status: 'ready', errorMessage: undefined });
  }

  /** The "disconnect" action -- clears the selection and tears down any open stream. Recording in progress, if any, is stopped and discarded by the caller before this runs (see TriageComponent's cancelStethoscopeSession()). */
  disconnect(): void {
    this.teardownStream();
    (navigator as any).mediaDevices?.removeEventListener('devicechange', this.boundRefreshDevices);
    this.hasEnumeratedOnce = false;
    this.knownDeviceIds = new Set();
    this.setState({ ...INITIAL_STATE });
  }

  async startRecording(): Promise<void> {
    this.setState({ ...this.stateSubject.value, errorMessage: undefined });
    const deviceId = this.stateSubject.value.selectedDeviceId;
    if (!deviceId) return;

    try {
      this.mediaStream = await this.openStreamForDevice(deviceId);
    } catch (err) {
      this.setState({ ...this.stateSubject.value, errorMessage: this.describeMediaError(err) });
      return;
    }

    const requestedMimeType = this.pickMimeType();
    this.recordingMimeType = requestedMimeType;
    this.recordedChunks = [];
    const MediaRecorderCtor = (window as any).MediaRecorder;
    this.mediaRecorder = requestedMimeType
      ? new MediaRecorderCtor(this.mediaStream, { mimeType: requestedMimeType })
      : new MediaRecorderCtor(this.mediaStream);
    // MediaRecorder.mimeType always reflects what the browser is actually
    // producing, even when the requested one wasn't honored exactly --
    // trusting it over the requested guess is what keeps the saved Blob's
    // declared type from lying about its real container format (a mismatch
    // there is what makes <audio> playback fail with "no supported sources"
    // even though the recording itself succeeded).
    const actualMimeType = this.mediaRecorder.mimeType;
    if (actualMimeType) this.recordingMimeType = actualMimeType;

    this.mediaRecorder.ondataavailable = (e: any) => {
      if (e.data && e.data.size > 0) this.recordedChunks.push(e.data);
    };
    this.stopPromise = new Promise<void>((resolve) => {
      this.mediaRecorder.onstop = () => resolve();
    });

    this.mediaRecorder.start();
    this.startedAtMs = Date.now();
    this.setState({ ...this.stateSubject.value, status: 'recording' });
  }

  /** Stops the recording and returns the finished Blob (or null if nothing was captured), then tears the audio stream down. */
  async stopRecording(): Promise<Blob | null> {
    if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') return null;

    this.mediaRecorder.stop();
    if (this.stopPromise) await this.stopPromise;
    this.stopPromise = null;
    this.mediaRecorder = null;

    const blob = new Blob(this.recordedChunks, { type: this.recordingMimeType || 'audio/webm' });
    this.recordedChunks = [];
    this.teardownStream();
    this.setState({ ...this.stateSubject.value, status: this.stateSubject.value.devices.length ? 'ready' : 'no-device' });

    return blob.size > 0 ? blob : null;
  }

  private teardownStream(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(t => t.stop());
      this.mediaStream = null;
    }
  }

  /** Opens the exact requested device, retrying once after a short settle delay if the OS/browser silently opened a different one instead (a realistic occurrence right after plugging in a USB device) -- ported unchanged from AaboOneRecorder's own openStreamForDevice(). */
  private async openStreamForDevice(deviceId: string): Promise<MediaStream> {
    for (let attempt = 1; attempt <= 2; attempt++) {
      const stream = await (navigator as any).mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
      });
      const track = stream.getAudioTracks()[0];
      const actualDeviceId = track ? track.getSettings().deviceId : undefined;
      const matched = !actualDeviceId || actualDeviceId === deviceId;

      if (matched) return stream;

      if (attempt === 1) {
        stream.getTracks().forEach((t: any) => t.stop());
        await this.wait(DEVICE_SETTLE_DELAY_MS);
        continue;
      }

      this.setState({
        ...this.stateSubject.value,
        errorMessage: 'The browser opened a different microphone than the one selected, even after retrying. This can happen right after plugging in a USB device -- try unplugging and replugging the Aabo Stethoscope, or check your system Sound settings to make sure nothing else is forcing a different default input device.'
      });
      return stream;
    }
    throw new Error('unreachable');
  }

  private wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async refreshDevices(): Promise<void> {
    if (!(navigator as any).mediaDevices) return;
    const devices: MediaDeviceInfo[] = await (navigator as any).mediaDevices.enumerateDevices();
    const inputs = devices.filter(d => d.kind === 'audioinput').map(d => ({ deviceId: d.deviceId, label: d.label || 'Unnamed device' }));
    const currentIds = new Set(inputs.map(d => d.deviceId));

    const isColdStart = !this.hasEnumeratedOnce;
    let newlyPlugged: { deviceId: string; label: string } | null = null;
    if (this.hasEnumeratedOnce) {
      newlyPlugged = inputs.find(d => !this.knownDeviceIds.has(d.deviceId)) || null;
    }
    this.knownDeviceIds = currentIds;
    this.hasEnumeratedOnce = true;

    if (inputs.length === 0) {
      this.setState({ ...this.stateSubject.value, status: 'no-device', devices: [], selectedDeviceId: null, errorMessage: undefined });
      return;
    }

    const isRecording = this.stateSubject.value.status === 'recording';
    const previousSelection = this.stateSubject.value.selectedDeviceId;
    const previousSelectionStillPresent = !!previousSelection && currentIds.has(previousSelection);

    let selected: string | null;
    if (isRecording) {
      selected = previousSelection;
    } else if (newlyPlugged) {
      selected = newlyPlugged.deviceId;
    } else if (previousSelectionStillPresent) {
      selected = previousSelection;
    } else if (previousSelection) {
      // The previously-selected device disappeared -- never silently
      // substitute a different microphone (could mean falling back to a
      // laptop's built-in mic without the clinician noticing). Require an
      // explicit re-pick instead.
      selected = null;
    } else if (isColdStart && inputs.length === 1) {
      selected = inputs[0].deviceId;
    } else {
      selected = null;
    }

    this.setState({
      ...this.stateSubject.value,
      status: isRecording ? 'recording' : 'ready',
      devices: inputs,
      selectedDeviceId: selected,
      errorMessage: !selected && previousSelection && !previousSelectionStillPresent
        ? 'Your stethoscope was disconnected. Choose a device from the list below to continue.'
        : this.stateSubject.value.errorMessage,
    });
  }

  private pickMimeType(): string {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
    ];
    const MediaRecorderCtor = (window as any).MediaRecorder;
    for (const type of candidates) {
      if (MediaRecorderCtor && MediaRecorderCtor.isTypeSupported && MediaRecorderCtor.isTypeSupported(type)) return type;
    }
    return '';
  }

  private describeMediaError(err: unknown): string {
    const name = (err as { name?: string } | undefined)?.name;
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      return 'Microphone permission was denied. Allow microphone access for this page in your browser settings and try again.';
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      return 'The selected device is no longer available -- it may have been unplugged. Reconnect it and try again.';
    }
    if (name === 'NotReadableError') {
      return 'The device could not be accessed. It may be in use by another application.';
    }
    const message = err instanceof Error ? err.message : String(err);
    return `Could not access the stethoscope: ${message}`;
  }

  private setState(state: StethoscopeState): void {
    this.stateSubject.next(state);
  }
}
